import React, { useEffect, useCallback, forwardRef, RefObject } from 'react';
import Loader from '../Loader';

interface EndlessScrollProps {
  items: [];
  loading: boolean;
  onLoadMore: () => void;
  isStopLoad: boolean;
  forwardedRef: RefObject<HTMLDivElement>;
  scrollContainer?: 'window' | 'parent';
}

const EndlessScroll: React.FC<
  EndlessScrollProps & { Component: React.ComponentType }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  scrollContainer = 'window',
  items,
  Component,
  ...props
}) => {
  const handleScroll = useCallback(() => {
    if (loading || isStopLoad) return;

    if (scrollContainer === 'parent') {
      const el = forwardedRef?.current;
      if (!el) return;

      const threshold = 150;
      const isNearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

      if (isNearBottom) {
        onLoadMore();
      }

      return;
    }

    if (!forwardedRef?.current) return;

    const { top, height } = forwardedRef.current.getBoundingClientRect();
    const offset = window.innerHeight * 2;

    if (
      top + height + offset >= 0 &&
      top + height - offset <= window.innerHeight
    ) {
      onLoadMore();
    }
  }, [onLoadMore, loading, isStopLoad, forwardedRef, scrollContainer]);

  useEffect(() => {
    const target =
      scrollContainer === 'parent' ? forwardedRef?.current : window;

    if (!target) return;

    target.addEventListener('scroll', handleScroll);

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollContainer, forwardedRef]);

  return (
    <>
      <Component {...props} forwardedRef={forwardedRef} />
      <Loader
        loading={loading && !!items.length && !isStopLoad}
        optionalClass="endless-loader"
      />
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll {...props} forwardedRef={ref} Component={Component} />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
